import { translate } from './i18n'

/**
 * En el teléfono, las hojas abiertas una encima de otra son UNA ventana.
 *
 * Abrir algo desde una hoja —un color desde un estilo, una columna desde el
 * pie, una plantilla desde el panel— apilaba otra hoja encima, y otra más:
 * popup sobre popup sobre popup, cada uno con su borde y su sombra asomando
 * detrás. Aquí se ordenan por cuándo aparecieron y sólo se enseña la de
 * arriba, del alto de la de abajo, como una pantalla más de la misma ventana:
 *
 * - «‹ Volver», encima de la ventana, cierra sólo la de arriba y enseña la
 *   anterior tal como estaba;
 * - la ✕ de la hoja cierra la ventana entera, no hoja por hoja.
 *
 * No toca ninguno de los componentes: cada hoja se sigue abriendo y cerrando
 * con su propia lógica. Para cerrar una se pulsa su propio botón de cerrar (el
 * de `aria-label` «Cerrar»), que es lo único que las cierra a ellas solas.
 */

const SHEETS = [
  '.app-body .sidebar',
  '.props-panel',
  '.brand-token-popup',
  '.side-panel-popover',
  '.template-popup',
  '.tb-popup-card',
  '.tb-flyout',
  '.icon-picker-popover',
  '.color-picker-popup',
].join(', ')

const COVERED = 'data-sheet-covered'
const TOP = 'data-sheet-top'

let installed = false
const firstSeen = new WeakMap<Element, number>()
let clock = 0
let nav: HTMLDivElement | null = null
let closingAll = false
/** El clic que damos nosotros al botón de cerrar: no es la ✕ pulsada a mano. */
let programmaticClick = false

function isPhone() {
  return document.documentElement.dataset.formFactor === 'phone'
}

function isShown(el: Element): boolean {
  const rect = el.getBoundingClientRect()
  if (rect.width < 2 || rect.height < 2) return false
  const style = getComputedStyle(el)
  if (style.display === 'none') return false
  // La tapada la escondemos nosotros: sigue contando como abierta.
  if (style.visibility === 'hidden' && !el.hasAttribute(COVERED)) return false
  if (Number(style.opacity) < 0.05) return false
  if (style.pointerEvents === 'none' && !el.hasAttribute(COVERED)) return false
  return true
}

/** Las hojas abiertas, de la primera que se abrió a la última. */
function openSheets(): HTMLElement[] {
  const all = Array.from(document.querySelectorAll<HTMLElement>(SHEETS))
  const shown = all.filter((el) => {
    // Una hoja dentro de otra es parte de ella, no una hoja más.
    const parent = el.parentElement?.closest(SHEETS)
    if (parent) return false
    if (!isShown(el)) { firstSeen.delete(el); return false }
    if (!firstSeen.has(el)) firstSeen.set(el, ++clock)
    return true
  })
  return shown.sort((a, b) => (firstSeen.get(a) ?? 0) - (firstSeen.get(b) ?? 0))
}

function closeButtonOf(sheet: HTMLElement): HTMLButtonElement | null {
  const label = translate('common.close')
  const buttons = Array.from(sheet.querySelectorAll<HTMLButtonElement>(
    `button[aria-label="${label}"], button.tb-popup-close, button.sidebar-close-btn`,
  ))
  return buttons.find((button) => button.closest(SHEETS) === sheet && !button.disabled) ?? null
}

/** Cierra sólo la hoja de arriba, con su propio botón. */
function closeTop(): boolean {
  const stack = openSheets()
  const top = stack[stack.length - 1]
  if (!top) return false
  const button = closeButtonOf(top)
  if (button) {
    programmaticClick = true
    try { button.click() } finally { programmaticClick = false }
    return true
  }
  if (top.matches('.app-body .sidebar')) {
    window.dispatchEvent(new CustomEvent('veltica:close-sidebar'))
    return true
  }
  document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }))
  return true
}

const nextFrame = () => new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())))

/** Cierra la ventana entera: hoja por hoja, de arriba abajo. */
async function closeAll() {
  if (closingAll) return
  closingAll = true
  try {
    for (let i = 0; i < 10; i += 1) {
      await nextFrame()
      if (openSheets().length === 0) break
      if (!closeTop()) break
    }
  } finally {
    closingAll = false
    sync()
  }
}

function ensureNav(): HTMLDivElement {
  if (nav && document.body.contains(nav)) return nav
  nav = document.createElement('div')
  nav.className = 'phone-sheet-nav'
  const back = document.createElement('button')
  back.type = 'button'
  back.className = 'phone-sheet-back'
  back.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 6-6 6 6 6"/></svg><span></span>'
  back.addEventListener('click', () => { closeTop() })
  nav.appendChild(back)
  /* Tocarlo no es «tocar fuera» para ninguna hoja: si el toque llegara al
     documento, las que se cierran al tocar fuera se cerrarían todas a la vez. */
  for (const type of ['pointerdown', 'mousedown', 'touchstart']) {
    nav.addEventListener(type, (event) => event.stopPropagation())
  }
  document.body.appendChild(nav)
  return nav
}

/**
 * La cabecera de una hoja y su título, para poner «‹» a su izquierda.
 *
 * Se encuentra desde su botón de cerrar: se sube hasta la primera fila cuyo
 * primer hijo no es un botón ni contiene la ✕ —el título—. Así sirve para
 * todas las cabeceras (`sidebar-header`, `tb-popup-header`,
 * `form-cfg-field-head`…) sin conocerlas una a una.
 */
function headerOf(sheet: HTMLElement): { header: HTMLElement; title: Element } | null {
  const close = closeButtonOf(sheet)
  if (!close) return null
  let el: HTMLElement | null = close.parentElement
  while (el && el !== sheet.parentElement) {
    const first = Array.from(el.children).find((child) => !child.classList.contains('phone-sheet-back'))
    if (first && !first.contains(close) && first.tagName !== 'BUTTON') return { header: el, title: first }
    if (el === sheet) break
    el = el.parentElement
  }
  return null
}

let inlineBack: HTMLButtonElement | null = null
const BACK_HOST = 'data-sheet-back-host'

function ensureInlineBack(): HTMLButtonElement {
  if (inlineBack) return inlineBack
  const button = document.createElement('button')
  button.type = 'button'
  button.className = 'phone-sheet-back is-inline'
  button.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 6-6 6 6 6"/></svg><span></span>'
  button.addEventListener('click', (event) => { event.stopPropagation(); closeTop() })
  for (const type of ['pointerdown', 'mousedown', 'touchstart']) {
    button.addEventListener(type, (event) => event.stopPropagation())
  }
  inlineBack = button
  return button
}

function removeInlineBack() {
  if (inlineBack?.parentElement) inlineBack.remove()
  for (const host of Array.from(document.querySelectorAll(`[${BACK_HOST}]`))) host.removeAttribute(BACK_HOST)
}

function sync() {
  const root = document.documentElement
  const stack = isPhone() ? openSheets() : []
  const top = stack.length >= 2 ? stack[stack.length - 1]! : null

  for (const el of Array.from(document.querySelectorAll(`[${COVERED}], [${TOP}]`))) {
    const keep = top && stack.includes(el as HTMLElement)
    if (!keep) { el.removeAttribute(COVERED); el.removeAttribute(TOP) }
  }
  if (!top) {
    removeInlineBack()
    if (nav && !nav.hidden) nav.hidden = true
    root.style.removeProperty('--sheet-stack-h')
    return
  }
  for (const el of stack) {
    const on = el === top ? TOP : COVERED
    const off = el === top ? COVERED : TOP
    if (!el.hasAttribute(on)) el.setAttribute(on, '')
    if (el.hasAttribute(off)) el.removeAttribute(off)
  }
  /* Del alto de la primera: es la misma ventana, y encoger o estirarse al
     pasar de una pantalla a otra se leía como abrir otra cosa. */
  const base = stack[0]!
  const height = `${Math.round(base.getBoundingClientRect().height)}px`
  if (root.style.getPropertyValue('--sheet-stack-h') !== height) root.style.setProperty('--sheet-stack-h', height)

  /* «‹» a la izquierda del título de la hoja de arriba: pequeño, sin texto,
     dentro de su cabecera. Sólo si la cabecera no se encuentra se usa la
     pastilla flotante de encima. */
  /* Si la hoja ya trae su propio «‹ Volver» (una pantalla interior del
     panel), no se pone otro al lado. */
  const ownBack = Array.from(top.querySelectorAll('.phone-sheet-back')).some((button) => button !== inlineBack)
  if (ownBack) {
    removeInlineBack()
    if (nav && !nav.hidden) nav.hidden = true
    return
  }
  const found = headerOf(top)
  if (found) {
    if (nav && !nav.hidden) nav.hidden = true
    const back = ensureInlineBack()
    const label = translate('common.back')
    const text = back.querySelector('span')
    if (text && text.textContent !== label) text.textContent = label
    for (const host of Array.from(document.querySelectorAll(`[${BACK_HOST}]`))) {
      if (host !== found.header) host.removeAttribute(BACK_HOST)
    }
    if (back.parentElement !== found.header || back.nextElementSibling !== found.title) {
      found.header.insertBefore(back, found.title)
    }
    if (!found.header.hasAttribute(BACK_HOST)) found.header.setAttribute(BACK_HOST, '')
    return
  }
  removeInlineBack()

  const bar = ensureNav()
  if (bar.hidden) bar.hidden = false
  const label = bar.querySelector('span')
  const text = translate('common.back')
  if (label && label.textContent !== text) label.textContent = text
  const rect = top.getBoundingClientRect()
  /* Sólo si cambia: el propio `nav` está en el cuerpo que se observa, y
     escribir lo mismo en cada cuadro volvería a despertar al observador. */
  const nextTop = `${Math.max(8, Math.round(rect.top - 44))}px`
  const nextLeft = `${Math.round(rect.left)}px`
  if (bar.style.top !== nextTop) bar.style.top = nextTop
  if (bar.style.left !== nextLeft) bar.style.left = nextLeft
}

let frame = 0
function schedule() {
  if (frame) return
  // En escritorio no hay pila que ordenar (salvo limpiar la que quedara).
  if (!isPhone() && !(nav && !nav.hidden) && !inlineBack?.parentElement && !document.querySelector(`[${COVERED}], [${TOP}]`)) return
  frame = requestAnimationFrame(() => { frame = 0; sync() })
}

export function installPhoneSheetStack() {
  if (installed || typeof document === 'undefined') return
  installed = true
  new MutationObserver(schedule).observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'],
  })
  window.addEventListener('resize', schedule)
  /* Las hojas entran con una animación que arranca en opacidad 0: en ese
     instante no cuentan como abiertas, y al terminar no cambia ningún atributo
     que despierte al observador. Se vuelve a mirar cuando acaba. */
  document.addEventListener('animationend', schedule, true)
  document.addEventListener('transitionend', schedule, true)

  /* La ✕ de la hoja de arriba, con otras debajo, cierra la ventana entera: su
     propio clic cierra la suya y aquí se cierran las demás. */
  document.addEventListener('click', (event) => {
    if (!isPhone() || closingAll || programmaticClick) return
    const target = event.target instanceof Element ? event.target.closest('button') : null
    if (!target) return
    const stack = openSheets()
    if (stack.length < 2) return
    const top = stack[stack.length - 1]!
    if (closeButtonOf(top) !== target) return
    void closeAll()
  }, true)
  schedule()
}
